import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { gridContainer, breakpoints } from 'styles';
import { LocalContext } from 'context';
import { Button } from 'components';
import { motion, AnimatePresence } from 'framer-motion';
import { wrap } from '@popmotion/popcorn';
import { graphql, useStaticQuery } from 'gatsby';
import LeftArrow from 'assets/svgs/left-arrow.svg';
import RightArrow from 'assets/svgs/right-arrow.svg';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import FB from 'assets/svgs/fb-icon.svg';
import Email from 'assets/svgs/email-icon.svg';
import IN from 'assets/svgs/linkedin-icon.svg';
import TW from 'assets/svgs/twitter-icon.svg';
import Commenter from 'assets/svgs/commenter.svg';

const variants = {
  enter: (direction) => {
    return {
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    };
  },
  center: {
    zIndex: 1,
    x: 0,
    opacity: 1
  },
  exit: (direction) => {
    return {
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    };
  }
};

const FinalistPreview = ({ startPoint }) => {
  const { mobile, theme } = useContext(LocalContext);
  const [[page, direction], setPage] = useState([startPoint, 0]);
  const data = useStaticQuery(
    graphql`
      query {
        allMarkdownRemark(
          filter: {
            fileAbsolutePath: { regex: "/(posters)/" }
            frontmatter: { finalist: { eq: true } }
          }
          sort: { order: ASC, fields: [frontmatter___id] }
        ) {
          nodes {
            id
            frontmatter {
              id
              name
              position
              fbLink
              twLink
              linkedinLink
              emailLink
              topics
              summary
              image {
                childImageSharp {
                  gatsbyImageData(
                    layout: CONSTRAINED
                    formats: [AUTO, WEBP, AVIF]
                    width: 450
                    placeholder: TRACED_SVG
                  )
                }
              }
            }
          }
        }
      }
    `
  );

  const paginate = (newDirection) => {
    setPage([page + newDirection, newDirection]);
  };

  const { nodes: finalists } = data.allMarkdownRemark;

  const finalistIndex = wrap(0, finalists.length, page);

  const finalist = finalists[finalistIndex].frontmatter;

  return (
    <Backdrop>
      {!mobile && (
        <Carousel
          theme={theme}
          key={page}
          custom={direction}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            x: { type: 'spring', stiffness: 300, damping: 50 },
            opacity: { duration: 0.2 }
          }}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={1}
          onDragEnd={(e, { offset, velocity }) => {
            const swipe = swipePower(offset.x, velocity.x);

            if (swipe < -swipeConfidenceThreshold) {
              paginate(1);
            } else if (swipe > swipeConfidenceThreshold) {
              paginate(-1);
            }
          }}>
          <AnimatePresence initial={false} custom={direction}>
            <FinalistContainer theme={theme}>
              <LiveStream>
                <StreamPlaceholder image={getImage(finalist.image.childImageSharp)} theme={theme} />
                <Prev theme={theme} onClick={() => paginate(-1)}>
                  <LeftArrow />
                </Prev>
              </LiveStream>
              <FinalistDetails>
                <FinalistName theme={theme}>{finalist.name}</FinalistName>
                <FinalistPosition>{finalist.position}</FinalistPosition>
                <FinalistSocials>
                  <FinalistSocial href={finalist.fbLink} theme={theme}>
                    <FB />
                  </FinalistSocial>
                  <FinalistSocial href={finalist.linkedinLink} theme={theme}>
                    <IN />
                  </FinalistSocial>
                  <FinalistSocial href={finalist.twLink} theme={theme}>
                    <TW />
                  </FinalistSocial>
                  <FinalistSocial href={finalist.emailLink} theme={theme}>
                    <Email />
                  </FinalistSocial>
                </FinalistSocials>
                <FinalistTopics>
                  {finalist.topics.map((topic, i) => (
                    <FinalistTopic key={i}>{topic}</FinalistTopic>
                  ))}
                </FinalistTopics>
                <FinalistSummary theme={theme}>{finalist.summary}</FinalistSummary>
                <ChatBox theme={theme}>{finalist.name}</ChatBox>
                <Next theme={theme} onClick={() => paginate(1)}>
                  <RightArrow />
                </Next>
              </FinalistDetails>
              <Comment>
                <CommentLabel htmlFor="comment" theme={theme}>
                  Add Your Comment
                </CommentLabel>
                <AddComment>
                  <Icon>
                    <Commenter />
                  </Icon>
                  <CommentInput
                    id="Finalist-comment"
                    name="comment"
                    type="text"
                    placeholder="Insert your comment..."
                    theme={theme}
                  />
                </AddComment>
              </Comment>
              {!mobile && (
                <Actions>
                  <Cancel>Cancel</Cancel>
                  <Button width="8.5rem">Comment</Button>
                </Actions>
              )}
            </FinalistContainer>
          </AnimatePresence>
        </Carousel>
      )}
      {mobile && (
        <Carousel theme={theme}>
          <FinalistContainer theme={theme}>
            <LiveStream>
              <StreamPlaceholder image={finalist.image.childImageSharp} theme={theme} />
              <Prev theme={theme} onClick={() => paginate(-1)}>
                <LeftArrow />
              </Prev>
            </LiveStream>
            <FinalistDetails>
              <FinalistName theme={theme}>{finalist.name}</FinalistName>
              <FinalistPosition>{finalist.position}</FinalistPosition>
              <FinalistSocials>
                <FinalistSocial href={finalist.fbLink} theme={theme}>
                  <FB />
                </FinalistSocial>
                <FinalistSocial href={finalist.linkedinLink} theme={theme}>
                  <IN />
                </FinalistSocial>
                <FinalistSocial href={finalist.twLink} theme={theme}>
                  <TW />
                </FinalistSocial>
                <FinalistSocial href={finalist.emailLink} theme={theme}>
                  <Email />
                </FinalistSocial>
              </FinalistSocials>
              <FinalistTopics>
                {finalist.topics.map((topic, i) => (
                  <FinalistTopic key={i}>{topic}</FinalistTopic>
                ))}
              </FinalistTopics>
              <FinalistSummary theme={theme}>{finalist.summary}</FinalistSummary>
              <ChatBox theme={theme}>{finalist.name}</ChatBox>
              <Next theme={theme} onClick={() => paginate(1)}>
                <RightArrow />
              </Next>
            </FinalistDetails>
            <Comment>
              <CommentLabel htmlFor="comment" theme={theme}>
                Add Your Comment
              </CommentLabel>
              <AddComment>
                <Icon>
                  <Commenter />
                </Icon>
                <CommentInput
                  id="Finalist-comment"
                  name="comment"
                  type="text"
                  placeholder="Insert your comment..."
                  theme={theme}
                />
              </AddComment>
            </Comment>
            {!mobile && (
              <Actions>
                <Cancel>Cancel</Cancel>
                <Button width="8.5rem">Comment</Button>
              </Actions>
            )}
          </FinalistContainer>
        </Carousel>
      )}
    </Backdrop>
  );
};

const swipeConfidenceThreshold = 10000;
const swipePower = (offset, velocity) => {
  return Math.abs(offset) * velocity;
};

const Backdrop = styled.div`
  max-width: 100%;
  height: auto;
  margin-top: -20px;
  margin-left: -20px;
  width: calc(100vw + 40px);
`;

const Carousel = styled(motion.div)`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: auto;
  min-height: 100vh;
  width: 100vw;
  color: ${({ theme }) => theme.textColor};
  background: ${({ theme }) => (theme.className === 'contrast' ? theme.bgLight : 'white')};
`;

const FinalistContainer = styled(motion.div)`
  position: relative;
  ${gridContainer}
  width:1200px;
  max-width: 100%;
  height: auto;
  padding: 3rem;
  align-items: center;
  color: ${({ theme }) => theme.textColor};
  @media (min-width: 1150px) {
    padding: 7.5rem;
  }
`;

const LiveStream = styled.div`
  grid-column: 1/7;
  height: auto;
  margin: 1.25rem -1.25rem;
  position: relative;
  @media (min-width: 768px) {
    grid-column: 1/3;
  }
  @media (min-width: ${breakpoints.lg}) {
    grid-column: 1/5;
    margin: 0px;
    height: 100%;
  }
`;

const StreamPlaceholder = styled(GatsbyImage)`
  width: 450px;
  max-width: 100%;
  height: auto;
  object-fit: cover;
  object-position: center;
  border-radius: ${({ cropped }) => (cropped ? '50%' : '0px')};
  transition: 0.5s;
  cursor: pointer;
  box-shadow: ${({ theme }) =>
    theme.className === 'contrast' ? 'none' : '0px.375rem 0.75rem rgba(0,0,0,.25)'};
`;

const FinalistDetails = styled.div`
  grid-column: 1/7;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  height: 100%;
  position: relative;
  color: ${({ theme }) => theme.textColor};
  @media (min-width: 768px) {
    grid-column: 4/7;
  }
  @media (min-width: ${breakpoints.lg}) {
    grid-column: 6/11;
  }
`;

const ChatBox = styled.div`
  width: 100%;
  height: 100%;
  margin-bottom: 0px;
  padding: 1.25rem;
  border: 1px solid;
  color: ${({ theme }) => theme.textColor};
  background: ${({ theme }) => (theme.className === 'contrast' ? theme.bgLight : 'white')};
`;
const Comment = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  margin: 2.5rem 0px;
  grid-column: 1/7;
  @media (min-width: ${breakpoints.lg}) {
    grid-column: 1/11;
  }
`;
const CommentLabel = styled.label`
  font-style: normal;
  font-weight: 400;
  font-size: 1.25rem;
  line-height: 2rem;
  color: ${({ theme }) => theme.textColor};
  width: 100%;
`;

const AddComment = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const CommentInput = styled.input`
  border: none;
  width: 100%;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  padding: 0.375rem;
  background: transparent;

  font-weight: 400;
  letter-spacing: 0.5px;
  font-size: 1rem;
  line-height: 2rem;
  color: ${({ theme }) => theme.textColor};
  border-bottom: 1px solid;
  border-color: ${({ theme }) => theme.textColor};
  ::placeholder {
    font-style: italic;
    font-weight: 400;
    font-size: 1rem;
    color: #bdbdbd;
  }
`;

const Icon = styled.div`
  grid-column: 1/2;
  width: 3.5rem;
  height: 3.5rem;
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const Actions = styled.div`
  grid-column: 9/12;
  display: flex;
  justify-content: flex-end;
  margin-bottom: 2.5rem;
`;

const Cancel = styled.button`
  outline: none;
  background: none;
  border: none;
  width: 7.5rem;
  height: 3rem;

  font-style: normal;
  font-weight: 700;
  font-size: 1rem;
  line-height: 1.25rem;
  display: flex;
  align-items: center;
  text-align: center;
  text-transform: uppercase;
  color: #5a5a5b;
  margin-right: 0.625rem;
`;
const Prev = styled.div`
  outline: none;
  border: none;
  cursor: pointer;
  width: 3rem;
  height: 3rem;
  display: none;
  justify-content: center;
  align-items: center;
  border-top-left-radius: 50%;
  border-bottom-left-radius: 50%;
  justify-self: end;
  background-color: ${({ theme }) => theme.fg};
  padding-left: 0.625rem;
  transition: 0.5s;
  opacity: 0.8;
  position: absolute;
  left: -4.5rem;
  top: 50%;
  transform: translateY(-50%);
  svg {
    path {
      stroke: ${({ theme }) => theme.bgLight};
    }
  }
  :hover {
    transform: translateY(-50%) scale(1.05);
    opacity: 1;
  }
  @media (min-width: ${breakpoints.lg}) {
    display: flex;
  }
`;

const Next = styled.div`
  outline: none;
  border: none;
  cursor: pointer;
  width: 3rem;
  height: 3rem;
  display: none;
  justify-content: center;
  align-items: center;
  border-top-right-radius: 50%;
  border-bottom-right-radius: 50%;
  justify-self: start;
  background-color: ${({ theme }) => theme.fg};
  padding-right: 0.625rem;
  transition: 0.5s;
  opacity: 0.8;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  right: -4.5rem;
  svg {
    path {
      stroke: ${({ theme }) => theme.bgLight};
    }
  }
  :hover {
    transform: translateY(-50%) scale(1.05);
    opacity: 1;
  }
  @media (min-width: ${breakpoints.lg}) {
    display: flex;
  }
`;

const FinalistSocials = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
`;

const FinalistSocial = styled.a`
  margin-right: 0.625rem;
  svg {
    circle {
      fill: ${({ theme }) => theme.textColor};
      transition: 0.5s;
    }
    path {
      fill: ${({ theme }) => theme.bgLight};
      transition: 0.5s;
    }
  }
  :last-child {
    svg {
      path {
        fill: none;
        stroke: ${({ theme }) => theme.bgLight};
        transition: 0.5s;
      }
    }
  }
  :hover {
    svg {
      circle {
        fill: ${({ theme }) => theme.fg};
      }
      path {
        fill: ${({ theme }) => theme.bgLight};
      }
    }
    :last-child {
      svg {
        path {
          fill: none;
          stroke: ${({ theme }) => theme.bgLight};
          transition: 0.5s;
        }
      }
    }
  }
`;

const FinalistTopics = styled.p`
  text-align: center;
  font-size: 1.25rem;
  margin-bottom: 1rem;
  margin-top: auto;
`;

const FinalistTopic = styled.span`
  margin-top: auto;
  margin-bottom: 0;
  margin-right: 0.375rem;
  font-size: 1.5rem;
  line-height: 2rem;
  &:after {
    content: ',';
  }
  &:last-child {
    &:after {
      content: '';
    }
  }
`;

const FinalistName = styled.h3`
  font-style: normal;
  font-weight: 700;
  font-size: 1.5rem;
  margin-top: 0px;
  margin-bottom: 0px;
  text-align: center;
  :after {
    content: ' - Finalist';
    color: ${({ theme }) => theme.fg};
  }
`;

const FinalistPosition = styled.p`
  margin-top: 0px;

  font-style: normal;
  font-weight: 400;
  font-size: 1rem;
  line-height: 2rem;
  margin-bottom: 0.375rem;
  text-align: center;
`;

const FinalistSummary = styled.p`
  font-style: normal;
  font-weight: 400;
  font-size: 1rem;
  line-height: 1.5rem;
  margin-bottom: 1.5rem;
`;

export default FinalistPreview;
